import { FormEvent, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api } from '../api/client';
import { OrgLayout } from '../components/OrgLayout';
import { RecordInfoButton } from '../components/RecordInfoButton';
import { Modal } from '../components/Modal';
import { useToast } from '../components/Toast';
import type { DepartmentDto, Institution, ProgramDto, ProgramType } from '../types/models';

// Program-type options. `value` is what the API expects; `label` is the uppercase display text.
const PROGRAM_TYPES: { value: ProgramType; label: string }[] = [
  { value: 'Diploma', label: 'DIPLOMA' },
  { value: 'MBA', label: 'MBA' },
  { value: 'PG', label: 'PG' },
  { value: 'UG', label: 'UG' },
  { value: 'Certification', label: 'CERTIFICATION' },
];
const typeLabel = (t: ProgramType | null) => PROGRAM_TYPES.find(x => x.value === t)?.label ?? '—';

const emptyForm = { institutionId: '', departmentId: '', code: '', name: '', type: '' };

export function ProgramPage() {
  const { orgId = '' } = useParams();
  const toast = useToast();

  const [insts, setInsts] = useState<Institution[]>([]);
  const [depts, setDepts] = useState<DepartmentDto[]>([]);
  const [programs, setPrograms] = useState<ProgramDto[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [showModal, setShowModal] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [form, setForm] = useState({ ...emptyForm });
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  // Filter bar (Institution → Department).
  const [filter, setFilter] = useState({ institutionId: '', departmentId: '' });

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId]);

  async function refresh() {
    try {
      const [i, d, p] = await Promise.all([
        api.listInstitutions(orgId), api.listDepartments(orgId), api.listPrograms(orgId),
      ]);
      setInsts(i); setDepts(d); setPrograms(p);
    } catch (e: any) {
      setError(e?.message ?? 'Failed to load programs.');
    }
  }

  // Departments belonging to the institution chosen in the form.
  const deptsForForm = useMemo(
    () => depts.filter(d => d.institutionId === form.institutionId),
    [depts, form.institutionId],
  );

  const hasDepartments = depts.length > 0;

  // Filter-bar cascades + the filtered grid. A program's institution is derived from its department.
  const deptsForFilter = useMemo(
    () => depts.filter(d => d.institutionId === filter.institutionId),
    [depts, filter.institutionId],
  );

  function setFilterField(key: keyof typeof filter, value: string) {
    setFilter(prev => {
      const next = { ...prev, [key]: value };
      if (key === 'institutionId') next.departmentId = '';
      return next;
    });
  }

  const filteredPrograms = useMemo(() => {
    if (!programs) return null;
    const instDeptIds = new Set(depts.filter(d => d.institutionId === filter.institutionId).map(d => d.id));
    return programs.filter(p =>
      (!filter.institutionId || instDeptIds.has(p.departmentId))
      && (!filter.departmentId || p.departmentId === filter.departmentId));
  }, [programs, depts, filter]);

  function setField(key: keyof typeof emptyForm, value: string) {
    setForm(prev => {
      const next = { ...prev, [key]: value };
      if (key === 'institutionId') next.departmentId = '';
      return next;
    });
  }

  function openCreate() {
    setFormError(null); setEditId(null); setForm({ ...emptyForm }); setShowModal(true);
  }

  function openEdit(p: ProgramDto) {
    setFormError(null);
    setEditId(p.id);
    const dept = depts.find(d => d.id === p.departmentId);
    setForm({
      institutionId: dept?.institutionId ?? '',
      departmentId: p.departmentId,
      code: p.code, name: p.name, type: p.type ?? '',
    });
    setShowModal(true);
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    setFormError(null);
    setBusy(true);
    try {
      const type = (form.type || null) as ProgramType | null;
      if (editId) await api.updateProgram(editId, form.code.trim(), form.name.trim(), type);
      else await api.createProgram(form.departmentId, form.code.trim(), form.name.trim(), type);
      setShowModal(false);
      await refresh();
      toast.success(editId ? 'Program updated.' : 'Program created.');
    } catch (e: any) {
      setFormError(e?.message ?? 'Failed to save program.');
    } finally {
      setBusy(false);
    }
  }

  async function remove(p: ProgramDto) {
    if (!confirm(`Delete program “${p.name}”? This cannot be undone.`)) return;
    setError(null);
    try {
      await api.deleteProgram(p.id);
      await refresh();
      toast.success('Program deleted.');
    } catch (e: any) {
      // Surfaces FK-violation conflicts (e.g. "still has N batch(es)").
      const msg = e?.message ?? 'Failed to delete program.';
      setError(msg);
      toast.error(msg);
    }
  }

  // A program's department code, and the institution code above it, for the grid columns.
  const deptCode = (departmentId: string) => depts.find(x => x.id === departmentId)?.code ?? '—';
  const instCode = (departmentId: string) => {
    const d = depts.find(x => x.id === departmentId);
    return insts.find(i => i.id === d?.institutionId)?.code ?? '—';
  };

  return (
    <OrgLayout>
      <div className="row" style={{ justifyContent: 'space-between' }}>
        <h1 style={{ margin: 0 }}>Program</h1>
        <button className="primary" onClick={openCreate} disabled={!hasDepartments}>+ Add program</button>
      </div>

      {!hasDepartments && programs && (
        <div className="card" style={{ marginTop: 12 }}>
          <div className="muted">
            Create an <Link to={`/org/${orgId}/institutions`}>institution</Link> and{' '}
            <Link to={`/org/${orgId}/departments`}>department</Link> first — programs live under a department.
          </div>
        </div>
      )}

      {error && <div className="error" style={{ margin: '12px 0' }}>{error}</div>}

      <div className="card" style={{ marginTop: 16 }}>
        <div className="row" style={{ gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <label style={{ flex: '1 1 220px' }}>
            <div className="muted">Institution</div>
            <select value={filter.institutionId} onChange={e => setFilterField('institutionId', e.target.value)}>
              <option value="">All</option>
              {insts.map(i => <option key={i.id} value={i.id}>{i.code} — {i.name}</option>)}
            </select>
          </label>
          <label style={{ flex: '1 1 220px' }}>
            <div className="muted">Department</div>
            <select value={filter.departmentId} disabled={!filter.institutionId}
                    onChange={e => setFilterField('departmentId', e.target.value)}>
              <option value="">All</option>
              {deptsForFilter.map(d => <option key={d.id} value={d.id}>{d.code} — {d.name}</option>)}
            </select>
          </label>
          {(filter.institutionId || filter.departmentId) && (
            <button onClick={() => setFilter({ institutionId: '', departmentId: '' })}>Clear</button>
          )}
        </div>
      </div>

      <div className="card" style={{ marginTop: 16, padding: 0, overflowX: 'auto' }}>
        <table className="data-table">
          <thead>
            <tr>
              <th style={{ width: 56 }}>S.no</th>
              <th>Institution</th>
              <th>Department</th>
              <th>Name</th>
              <th>Code</th>
              <th>Type</th>
              <th style={{ width: 180 }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {!programs && <tr><td colSpan={6} className="muted">Loading…</td></tr>}
            {programs && filteredPrograms && filteredPrograms.length === 0 && (
              <tr><td colSpan={7} className="muted">
                {programs.length === 0 ? 'No programs yet.' : 'No programs match the selected filters.'}
              </td></tr>
            )}
            {filteredPrograms?.map((p, idx) => (
              <tr key={p.id}>
                <td>{idx + 1}</td>
                <td>{instCode(p.departmentId)}</td>
                <td>{deptCode(p.departmentId)}</td>
                <td style={{ fontWeight: 600 }}>{p.name}</td>
                <td className="slug">{p.code}</td>
                <td>{typeLabel(p.type)}</td>
                <td>
                  <button className="link-btn" onClick={() => openEdit(p)}>Edit</button>
                  <button className="link-btn" style={{ color: 'var(--danger)' }} onClick={() => remove(p)}>Delete</button>
                  <RecordInfoButton entity="programs" id={p.id} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showModal && (
        <Modal title={editId ? 'Edit program' : 'Create program'} onClose={() => setShowModal(false)}>
          <form onSubmit={submit} className="col" style={{ marginTop: 16 }}>
            <div className="row" style={{ gap: 12 }}>
              <label style={{ flex: 1 }}>
                <div className="muted">Institution *</div>
                <select value={form.institutionId} required disabled={!!editId}
                        onChange={e => setField('institutionId', e.target.value)}>
                  <option value="" disabled>Select…</option>
                  {insts.map(i => <option key={i.id} value={i.id}>{i.code} — {i.name}</option>)}
                </select>
              </label>
              <label style={{ flex: 1 }}>
                <div className="muted">Department *</div>
                <select value={form.departmentId} required disabled={!!editId || !form.institutionId}
                        onChange={e => setField('departmentId', e.target.value)}>
                  <option value="" disabled>{form.institutionId ? 'Select…' : 'Pick institution first'}</option>
                  {deptsForForm.map(d => <option key={d.id} value={d.id}>{d.code} — {d.name}</option>)}
                </select>
              </label>
            </div>
            <div className="row" style={{ gap: 12 }}>
              <label style={{ flex: '0 0 140px' }}>
                <div className="muted">Code *</div>
                <input value={form.code} required maxLength={32}
                       onChange={e => setField('code', e.target.value)} placeholder="BE-CSE" />
              </label>
              <label style={{ flex: 1 }}>
                <div className="muted">Name *</div>
                <input value={form.name} required maxLength={200}
                       onChange={e => setField('name', e.target.value)} placeholder="B.E. Computer Science" />
              </label>
            </div>
            <label>
              <div className="muted">Program Type *</div>
              <select value={form.type} required onChange={e => setField('type', e.target.value)}>
                <option value="" disabled>--Choose Program Type--</option>
                {PROGRAM_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </label>
            {formError && <div className="error">{formError}</div>}
            <div className="modal-actions">
              <button type="button" onClick={() => setShowModal(false)}>Cancel</button>
              <button type="submit" className="primary" disabled={busy}>
                {busy ? 'Saving…' : editId ? 'Save changes' : 'Create program'}
              </button>
            </div>
          </form>
        </Modal>
      )}
    </OrgLayout>
  );
}
